/**
 * Slice 37 — property Walk quick checks (unlimited).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";

export interface WalkQuickCheck {
  id: string;
  property_id: string;
  label: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function useWalkQuickChecks(propertyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.walkQuickChecks.byProperty(propertyId ?? ""),
    queryFn: async (): Promise<WalkQuickCheck[]> => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("property_walk_quick_checks")
        .select("*")
        .eq("property_id", propertyId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as WalkQuickCheck[];
    },
    enabled: !!propertyId,
  });
}

export function useCreateWalkQuickCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { propertyId: string; label: string }) => {
      const { data: existing, error: listErr } = await supabase
        .from("property_walk_quick_checks")
        .select("id")
        .eq("property_id", input.propertyId);
      if (listErr) throw listErr;
      const label = input.label.trim();
      if (!label) throw new Error("Label required");
      if (label.length > 40) throw new Error("Keep it to 40 characters");

      const { data, error } = await supabase
        .from("property_walk_quick_checks")
        .insert({
          property_id: input.propertyId,
          label,
          sort_order: (existing ?? []).length,
        })
        .select()
        .single();
      if (error) throw error;
      return data as WalkQuickCheck;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.walkQuickChecks.byProperty(row.property_id),
      });
    },
  });
}

export function useUpdateWalkQuickCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; propertyId: string; label: string }) => {
      const label = input.label.trim();
      if (!label) throw new Error("Label required");
      if (label.length > 40) throw new Error("Keep it to 40 characters");
      const { data, error } = await supabase
        .from("property_walk_quick_checks")
        .update({ label })
        .eq("id", input.id)
        .select()
        .single();
      if (error) throw error;
      return data as WalkQuickCheck;
    },
    onSuccess: (_row, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.walkQuickChecks.byProperty(variables.propertyId),
      });
    },
  });
}

export function useDeleteWalkQuickCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; propertyId: string }) => {
      const { error } = await supabase
        .from("property_walk_quick_checks")
        .delete()
        .eq("id", input.id);
      if (error) throw error;
      return input;
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.walkQuickChecks.byProperty(variables.propertyId),
      });
    },
  });
}
